/* eslint-disable @typescript-eslint/no-unsafe-return */
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitErrorHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  PostManuscriptsCreateDomMutationResult,
  PostManuscriptsCreateWebOmMutationResult,
  useGetManuscriptsInfo,
  useGetManuscriptsSettings,
  usePostManuscriptsCancel,
  usePostManuscriptsCheckCreateOri,
  usePostManuscriptsCreateDom,
  usePostManuscriptsCreateDomCheck,
  usePostManuscriptsCreateNewOri,
  usePostManuscriptsCreateOri,
  usePostManuscriptsCreateWebOm,
  usePostManuscriptsCreateWebomCheck,
  usePostManuscriptsLock,
  usePostManuscriptsOutputFrame,
  usePostManuscriptsOutputReference,
  usePostManuscriptsRevision,
  usePostManuscriptsSettingsSave,
} from '../../../../api/manuscripts/manuscripts';
import {
  PostTranslationsExportXmlMutationResult,
  usePostTranslationsExportXml,
} from '../../../../api/translations/translations';

import { useGetChapterNumbers } from '../../../../api/masters/masters';
import { ErrorInfo, ManuscriptRevisionItem, ProductionManuscriptsSetting } from '../../../../api/model';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { postWithKeepAlive } from '../../../../utils/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import ErrorComponent from '../../../atoms/ErrorComponent';
import Link from '../../../atoms/Link';
import SingleSelect from '../../../atoms/SingleSelect';
import WaitComponent from '../../../atoms/WaitComponent';
import AnotherTabLockingErrorModal from '../../../modules/AnotherTabLockingErrorModal';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmInputModal from '../../../modules/ConfirmInputModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SlideToggle from '../../../modules/SlideToggle';
import SpinnerModal from '../../../modules/SpinnerModal';
import FListUploadModal from './FListUploadModal';
import { CancelButton } from './FormElements';
import ManuscriptsTabOutputCommand from './ManuscriptsTabOutputCommand';
import ProductionManuscriptsPsSituationList from './ProductionManuscriptsPsSituationList';

interface Props {
  productionId: number;
  editModeTabIndex: number | undefined;
  setEditable: (editable: boolean) => void;
  refetchFirstId: () => void;
  firstSectionId: number | undefined;
}

/**
 * 原稿タブ
 * @param param0
 * @returns
 */
function ProductionManuscriptsTab({
  productionId,
  editModeTabIndex,
  setEditable,
  refetchFirstId,
  firstSectionId,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // バリデーション用のスキーマ
  const schema = yup.object().shape({
    production_id: yup.number().required(),
    include_navi: yup.boolean().required(),
    navi_production_no: yup
      .string()
      .nullable()
      .transform((v) => (v ? String(v) : undefined))
      .default(undefined),
    l1_title_jp: yup
      .string()
      .nullable()
      .transform((v) => (v ? String(v) : undefined))
      .default(undefined),
    l1_title_en: yup
      .string()
      .nullable()
      .transform((v) => (v ? String(v) : undefined))
      .default(undefined),
    apply_stretch: yup.boolean().required(),
    font_stretch: yup
      .number()
      .typeError(String(t('1-100までの整数を入力してください。')))
      .integer(String(t('1-100までの整数を入力してください。。')))
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === '' || originalValue === null ? undefined : value
      )
      .min(1, String(t('1-100までの整数を入力してください。')))
      .max(100, String(t('1-100までの整数を入力してください。')))
      .default(undefined),
    line_height: yup
      .number()
      .typeError(String(t('1-100までの整数を入力してください。')))
      .integer(String(t('1-100までの整数を入力してください。')))
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === '' || originalValue === null ? undefined : value
      )
      .min(1, String(t('1-100までの整数を入力してください。')))
      .max(100, String(t('1-100までの整数を入力してください。')))
      .default(undefined),
  });

  const options = useDefaultOptions(language);
  const navigate = useNavigate();
  const [openFListUploader, setOpenFListUploader] = useState(false); // F-list取込みダイアログの表示制御
  const [isOri, setIsOri] = useState<boolean | undefined>(false); // 原稿編集画面へ移動リンク
  const [isOriCopy, setIsOriCopy] = useState<boolean | undefined>(false); // ORIデータ複製ボタン
  const [isOriNew, setIsOriNew] = useState<boolean | undefined>(false); // ORIデータ新規作成ボタン
  const [isFlist, setIsFlist] = useState<boolean | undefined>(false); // F-LIST判定結果ボタン
  const [isImpFlist, setIsImpFlist] = useState<boolean | undefined>(false); // F-LIST取込み権限ボタン
  const [open, setOpen] = useState(false); // 基本情報が開いているかどう
  const [openRev, setOpenRev] = useState(false); // 基本情報が開いているかどう
  const [openPs, setOpenPs] = useState(false); // PS改廃一覧の開閉
  const editable = useMemo(() => editModeTabIndex === 5, [editModeTabIndex]); // 編集状態かどうか
  const [isCustomize, setIsCustomize] = useState<boolean>(false); // F-LIST判定結果ボタン

  // 設定情報取得
  const { data: settingData, refetch: refetchSetting } = useGetManuscriptsSettings(productionId, options);

  // 基本情報表示
  const {
    data: msInfoData,
    isError: msInfoIsError,
    error: msInfoError,
    isLoading: msInfoIsLoading,
  } = useGetManuscriptsInfo(productionId, options);
  useEffect(() => {
    if (msInfoData?.data == null) {
      setIsImpFlist(false);
      setIsFlist(false);
      setIsOri(false);
      setIsOriCopy(false);
      setIsOriNew(false);
    } else {
      setIsImpFlist(msInfoData.data.is_imp_flist);
      setIsFlist(msInfoData.data.is_flist);
      setIsOri(msInfoData.data.is_ori);
      setIsOriCopy(msInfoData.data.is_ori_copy);
      setIsOriNew(msInfoData.data.is_ori_new);
    }
  }, [msInfoData?.data]);

  // フォームを利用できるようにする
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProductionManuscriptsSetting>({
    defaultValues: settingData?.data.manuscript_setting,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(settingData?.data.manuscript_setting);
    if (
      settingData?.data.manuscript_setting?.apply_stretch === undefined ||
      settingData?.data.manuscript_setting?.apply_stretch === null
    ) {
      setIsCustomize(false);
    } else {
      setIsCustomize(settingData?.data.manuscript_setting?.apply_stretch);
    }
  }, [reset, settingData]); // データが変更されたらリセットする

  // ロックを取得する setInterval の返す ID
  const refLockIntervalId = useRef<NodeJS.Timer>();
  useEffect(() => () => clearInterval(refLockIntervalId.current), []);

  // beforeUnloadEvent（ブラウザを閉じようとしたときに、メッセージ表示）
  const beforeUnload = useCallback((e: BeforeUnloadEvent) => {
    e.returnValue = '';
  }, []);

  // コンポーネントが表示されなくなる時に、beforeunload イベントの解除
  useEffect(() => () => window.removeEventListener('beforeunload', beforeUnload), [beforeUnload]);

  // unload 関数（ブラウザを閉じるときに発生して、ロック解除するためのもの）
  const unload = useCallback(async () => {
    await postWithKeepAlive(`/manuscripts/cancel/${productionId}`, language);
  }, [language, productionId]);

  const refUnload = useRef<() => Promise<void>>();

  // コンポーネントが表示されなくなる時に、unload イベントの実行と解除
  useEffect(
    () => () => {
      if (refUnload.current != null) {
        void refUnload.current().then(() => {
          if (refUnload.current != null) {
            window.removeEventListener('unload', refUnload.current);
          }
        });
      }
    },
    []
  );

  const optionsForBinary = useOptionsForBinary(language);

  // 改訂履歴一覧
  const handleOpenRev = () => {
    setOpenRev(!openRev);
  };

  // PS改廃一覧
  const handleOpenPs = () => {
    setOpenPs(!openPs);
  };

  // #region 改訂履歴取得
  const [selectChapterNumberId, setSelectChapterNumberId] = useState(0);
  const chapterData = useGetChapterNumbers(options);
  const [revErrorModal, setRevErrorModal] = useState(false);
  // API
  const {
    data: revData,
    mutateAsync: revAsync,
    error: revError,
    isError: revIsError,
    isLoading: revIsLoading,
  } = usePostManuscriptsRevision<AxiosError<ErrorInfo>, unknown>(options);

  // 改訂履歴取得
  useEffect(() => {
    const postRev = async () => {
      await revAsync({ productionId: Number(productionId), chapterNumberId: selectChapterNumberId });
    };
    void postRev();
  }, [revAsync, productionId, selectChapterNumberId]);

  // 改訂履歴取得エラー表示
  useEffect(() => {
    setRevErrorModal(revIsError);
  }, [revIsError]);

  // データのソート
  const [sortInfo, setSortInfo] = useState({ sortkey: '' as keyof ManuscriptRevisionItem, isDesc: false });
  const revDataSorted = useMemo(
    () =>
      (revData?.data.manuscript_rivisions ?? []).sort((a, b) => {
        const propertyValueA = a[sortInfo.sortkey] ?? '';
        const propertyValueB = b[sortInfo.sortkey] ?? '';
        const direction = sortInfo.isDesc ? -1 : 1;

        if (propertyValueA < propertyValueB) {
          return -1 * direction;
        }
        if (propertyValueA > propertyValueB) {
          return 1 * direction;
        }
        return 0;
      }),
    [revData?.data.manuscript_rivisions, sortInfo.isDesc, sortInfo.sortkey]
  );
  // ソートクリック時の処理
  const onSortClicked = useCallback((key: keyof ManuscriptRevisionItem) => {
    setSortInfo((preSortInfo) => ({ sortkey: key, isDesc: preSortInfo.sortkey === key && !preSortInfo.isDesc }));
  }, []);
  // #endregion

  // #region ロック関連
  const { mutateAsync: lockAsync, error: lockError } = usePostManuscriptsLock(options); // ロック API
  const [displayLockError, setDisplayLockError] = useState(false); // ロックエラーを表示するかどうか
  const [displayAnotherTabLockError, setDisplayAnotherTabLockError] = useState(false); // 他のタブがロックされていることのエラー

  // ロックエラーが設定された場合は表示
  useEffect(() => {
    setDisplayLockError(lockError != null);
    setDisplaySpinnerModal(false);
  }, [lockError]);

  // 編集をやめる
  const onFinishEditing = useCallback(() => {
    setEditable(false); // 編集モードをやめる
    clearInterval(refLockIntervalId.current); // ロックの定期実行をやめる
    // ブラウザを閉じた時などのイベントを解除
    window.removeEventListener('beforeunload', beforeUnload);
    if (refUnload.current != null) {
      window.removeEventListener('unload', refUnload.current);
    }
    reset(settingData?.data.manuscript_setting); // フォームをリセット
    setDisplaySpinnerModal(false); // スピナーモーダルを非表示
  }, [beforeUnload, settingData, reset, setEditable]);

  // 編集モードにする
  const edit = useCallback(async () => {
    // 自身が編集モードのときは何もしない
    if (editModeTabIndex === 5) return;

    // 編集モードのタブがある場合はエラー表示
    if (editModeTabIndex != null) {
      setDisplayAnotherTabLockError(true);
      return;
    }
    setDisplaySpinnerModal(true); // スピナーモーダルの表示

    // 成功した場合の処理
    const onSuccess = async () => {
      // 表示するデータをリフェッチする
      try {
        await refetchSetting();

        // 定期的にロック API をたたく
        const lockInterval = parseInt(process.env.REACT_APP_GET_LOCK_INTERVAL ?? '10000', 10);
        refLockIntervalId.current = setInterval(() => {
          void lockAsync({ productionId }, { onError: onFinishEditing });
        }, lockInterval);

        // ブラウザを閉じた時などのロック解除を設定
        window.addEventListener('beforeunload', beforeUnload);
        refUnload.current = unload;
        window.addEventListener('unload', refUnload.current);

        setEditable(true); // 編集モードにする
        setOpen(true);
      } catch {
        // Do nothing
      } finally {
        setDisplaySpinnerModal(false); // スピナーモーダルを非表示に
      }
    };

    // 失敗時の処理（スピナーモーダルを非表示に）
    const onError = () => setDisplaySpinnerModal(false);
    // ロック API にアクセス
    await lockAsync({ productionId }, { onSuccess, onError });
  }, [
    beforeUnload,
    editModeTabIndex,
    lockAsync,
    onFinishEditing,
    productionId,
    refetchSetting,
    setEditable,
    unload,
    setOpen,
  ]);

  // #endregion

  // #region キャンセル関連
  const { mutateAsync: cancelAsync, error: cancelError } = usePostManuscriptsCancel(options); // キャンセル API
  const [displayCancelError, setDisplayCancelError] = useState(false); // キャンセルエラーを表示するかどうか

  // キャンセルエラーが設定された場合は表示
  useEffect(() => {
    setDisplayCancelError(cancelError != null);
    setDisplaySpinnerModal(false);
  }, [cancelError]);

  // キャンセルエラーが設定された場合は表示
  useEffect(() => {
    setDisplayCancelError(cancelError != null);
    setDisplaySpinnerModal(false);
  }, [cancelError]);

  // 編集モードをキャンセル
  const cancel = useCallback(async () => {
    setDisplaySpinnerModal(true); // スピナーモーダルの表示
    const onError = () => setDisplaySpinnerModal(false); // 成功時の処理（スピナーモーダルを非表示に）
    await cancelAsync({ productionId }, { onSuccess: onFinishEditing, onError }); // ロックキャンセル API をたたく
    setOpen(false);
  }, [cancelAsync, productionId, onFinishEditing, setOpen]);

  // #endregion

  // #region 保存ボタン押下時の動作
  const [displayConfirmSavingMessage, setDisplayConfirmSavingMessage] = useState(false); // 保存確認メッセージを表示するかどうか
  // 保存確認
  const confirmSaving = useCallback(async () => {
    const onValid = () => setDisplayConfirmSavingMessage(true);
    await handleSubmit(onValid)();
  }, [handleSubmit]);

  const { mutateAsync: saveAsync, error: saveError } = usePostManuscriptsSettingsSave(options); // 保存 API
  const [displaySaveError, setDisplaySaveError] = useState(false); // 保存時エラーを表示するかどうか
  // 保存時エラーが設定された場合は表示
  useEffect(() => {
    setDisplaySaveError(saveError != null);
    setDisplaySpinnerModal(false);
  }, [saveError]);

  const [displayValidationError, setDisplayValidationError] = useState(false); // バリデーションエラーを表示するかどうか
  const [displayCompleteSavingMessage, setDisplayCompleteSavingMessage] = useState(false); // 保存完了メッセージ表示するかどうか

  // 保存ボタン押下時の動作
  const submit = useCallback(async () => {
    setDisplayConfirmSavingMessage(false); // 確認モーダルを非表示
    setDisplaySpinnerModal(true); // スピナーモーダルを表示
    // バリデーション結果が有効だった場合
    const onValid = async (nextData: ProductionManuscriptsSetting) => {
      // 成功した場合の処理
      const onSuccess = async () => {
        // ブラウザを閉じた時などのイベントを解除
        window.removeEventListener('beforeunload', beforeUnload);
        if (refUnload.current != null) {
          window.removeEventListener('unload', refUnload.current);
        }

        try {
          await refetchSetting(); // 保存成功したらリフェッチする
          setEditable(false); // 編集状態を解除
          setOpen(false);
          clearInterval(refLockIntervalId.current); // ロック API の呼び出しを解除
          setDisplayCompleteSavingMessage(true); // 保存完了したことをメッセージ表示
        } catch {
          // Do nothing
        } finally {
          setDisplaySpinnerModal(false);
        }
      };

      // 成功失敗共通の処理（スピナーモーダルを非表示に）
      const onError = () => setDisplaySpinnerModal(false);

      // 保存実行
      await saveAsync(
        {
          productionId,
          data: nextData,
        },
        { onSuccess, onError }
      );
    };

    // バリデーション結果が無効だった場合（エラーモーダルを表示）
    const onInvalid: SubmitErrorHandler<ProductionManuscriptsSetting> = () => {
      setDisplaySpinnerModal(false); // スピナーモーダルを非表示
      setDisplayValidationError(true); // バリデーションエラーをモーダルで表示
    };

    // フォーム送信時の処理を設定
    await handleSubmit(onValid, onInvalid)();
  }, [beforeUnload, handleSubmit, refetchSetting, saveAsync, setEditable, productionId, setOpen]);

  // #endregion

  // #region 参照リスト
  // 定義
  const [displayManuscriptsOutputReferError, setDisplayManuscriptsOutputReferError] = useState(false);
  const { mutateAsync: manuscriptsOutputReferAsync, error: manuscriptsOutputReferError } =
    usePostManuscriptsOutputReference<AxiosError<Blob>>(optionsForBinary);

  useEffect(() => {
    setDisplayManuscriptsOutputReferError(manuscriptsOutputReferError != null);
  }, [manuscriptsOutputReferError]);

  // ダウンロード処理
  const onOutputReferClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await manuscriptsOutputReferAsync({ productionId });
    if (response.status === 200) {
      executeDownload(response);
      setDisplaySpinnerModal(false);
    } else {
      setDisplaySpinnerModal(false);
    }
  }, [productionId, manuscriptsOutputReferAsync]);
  // #endregion

  // #region Dom関連
  // Dom取得前チェック処理定義
  const { mutateAsync: manuscriptsCreateDomCheckAsync, error: manuscriptsCreateDomCheckError } =
    usePostManuscriptsCreateDomCheck(options);
  const [displayManuscriptsCreateDomCheckError, setDisplayManuscriptsCreateDomCheckError] = useState(false);
  useEffect(() => {
    setDisplayManuscriptsCreateDomCheckError(manuscriptsCreateDomCheckError != null);
  }, [manuscriptsCreateDomCheckError]);

  // Dom取得処理定義
  const { mutateAsync: manuscriptsCreateDomAsync, error: manuscriptsCreateDomError } = usePostManuscriptsCreateDom<
    AxiosError<Blob>,
    PostManuscriptsCreateDomMutationResult
  >(optionsForBinary);
  const [displayManuscriptsCreateDomError, setDisplayManuscriptsCreateDomError] = useState(false);

  useEffect(() => {
    setDisplayManuscriptsCreateDomError(manuscriptsCreateDomError != null);
  }, [manuscriptsCreateDomError]);

  // Domダウンロード処理
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [displayConfirmDomDownloadErr, setDisplayConfirmDomDownloadErr] = useState(false);
  const [displayConfirmDomDownload, setDisplayConfirmDomDownload] = useState(false);
  const onDomDownloadClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await manuscriptsCreateDomCheckAsync({ productionId });
    setDisplaySpinnerModal(false);
    if (!response.data.has_required_data) {
      setDisplayConfirmDomDownloadErr(true);
      return;
    }
    setDisplayConfirmDomDownload(true);
  }, [productionId, manuscriptsCreateDomCheckAsync]);

  const domDownload = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await manuscriptsCreateDomAsync({ productionId });
    setDisplaySpinnerModal(false);
    if (response.data != null) {
      executeDownload(response);
    }
  }, [productionId, manuscriptsCreateDomAsync]);
  // #endregion

  // #region Webom関連

  const [webType, setWebType] = useState(30);

  // WebOm取得前チェック処理定義
  const { mutateAsync: manuscriptsCreateWebomCheckAsync, error: manuscriptsCreateWebomCheckError } =
    usePostManuscriptsCreateWebomCheck(options);
  const [displayManuscriptsCreateWebomCheckError, setDisplayManuscriptsCreateWebomCheckError] = useState(false);
  const [displayConfirmWebomDownloadErr, setDisplayConfirmWebomDownloadErr] = useState(false);

  useEffect(() => {
    setDisplayManuscriptsCreateWebomCheckError(manuscriptsCreateWebomCheckError != null);
  }, [manuscriptsCreateWebomCheckError]);

  // WebOm取得処理定義
  const { mutateAsync: manuscriptsCreateWebOmAsync, error: manuscriptsCreateWebOmError } =
    usePostManuscriptsCreateWebOm<AxiosError<Blob>, PostManuscriptsCreateWebOmMutationResult>(optionsForBinary);
  const [displayManuscriptsCreateWebOmError, setDisplayManuscriptsCreateWebOmError] = useState(false);
  useEffect(() => {
    setDisplayManuscriptsCreateDomError(manuscriptsCreateWebOmError != null);
  }, [manuscriptsCreateWebOmError]);

  // WebOmダウンロード処理
  const [displayConfirmWebomDownload, setDisplayConfirmWebomDownload] = useState(false);
  const onWebOmDownloadClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await manuscriptsCreateWebomCheckAsync({ productionId });
    setDisplaySpinnerModal(false);
    if (!response.data.has_required_data) {
      setDisplayConfirmWebomDownloadErr(true);
      return;
    }
    setDisplayConfirmWebomDownload(true);
  }, [productionId, manuscriptsCreateWebomCheckAsync]);

  const webomDownload = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await manuscriptsCreateWebOmAsync({ productionId, data: { document_type: webType } });
    setDisplaySpinnerModal(false);
    if (response.data != null) {
      executeDownload(response);
    }
  }, [productionId, manuscriptsCreateWebOmAsync, webType]);
  // #endregion

  // #region ORIデータ作成処理
  const [displayConfirmStartCreateOri, setDisplayConfirmStartCreateOri] = useState(false);
  const [displayConfirmCopyOri, setDisplayConfirmCopyOri] = useState(false); // ORI複製時の2つ目の確認ダイアログ
  const [displayConfirmInputCopyOri, setDisplayConfirmInputCopyOri] = useState(false); // ORI複製時の2つ目の確認ダイアログ
  const [displayOriError, setOriErrorModal] = useState(false); // oriデータ作成時のエラーモーダルを表示する
  const [displayOriSpinner, setOriSpinnerModal] = useState(false); // oriデータ作成時のスピナーモーダルを表示する
  const [displayConfirmCheckCopyOri, setDisplayConfirmCheckCopyOri] = useState(false); // 複製前チェック時の確認ダイアログ
  const [displayCheckOriError, setCheckOriErrorModal] = useState(false); // oriデータ作成時のエラーモーダルを表示する

  const {
    mutateAsync: oriAsync,
    error: oriError,
    isError: isOriError,
    isLoading: isOriloading,
  } = usePostManuscriptsCreateOri<AxiosError<ErrorInfo>, unknown>(options);

  const {
    mutateAsync: oriCheckAsync,
    error: oriCheckError,
    isError: isOriCheckError,
    isLoading: isOriCheckloading,
  } = usePostManuscriptsCheckCreateOri<AxiosError<ErrorInfo>, unknown>(options);

  // ORIデータ複製エラー表示
  useEffect(() => {
    setOriErrorModal(isOriError);
    setOriSpinnerModal(isOriloading);
  }, [isOriError, isOriloading]);

  useEffect(() => {
    if (isOriCheckError) {
      if (oriCheckError.response !== undefined) {
        if (
          oriCheckError.response.data.error_code === 'ManuscriptNotLangrelationFile' ||
          oriCheckError.response.data.error_code === 'ManuscriptNotLangIllust'
        ) {
          setDisplayConfirmCheckCopyOri(true);
        } else {
          setCheckOriErrorModal(isOriCheckError);
        }
      }
    }
  }, [isOriCheckError, isOriCheckloading, oriCheckError]);

  // ORIデータ複製
  const createOri = useCallback(async () => {
    setOriSpinnerModal(true);
    const oriRes = await oriAsync({ productionId: Number(productionId) }); // ORIデータ複製
    if (oriRes.status === 200) {
      // 原稿編集へリダイレクト
      navigate(`../sections/${productionId}/ps?section_id=${Number(oriRes.data.first_section_id)}`); // 画面遷移
    }
  }, [oriAsync, productionId, navigate]);

  // ORIデータ複製前チェック
  const checkCopyOri = useCallback(async () => {
    const oriCheckRes = await oriCheckAsync({ productionId: Number(productionId) });
    if (oriCheckRes.status === 200) {
      void createOri();
    }
  }, [oriCheckAsync, productionId, createOri]);

  const onClickCopyOri = useCallback(() => {
    if (isOriCopy) {
      if (isOri) {
        setDisplayConfirmInputCopyOri(true);
      } else {
        setDisplayConfirmCopyOri(true);
      }
      return;
    }
    void checkCopyOri();
  }, [setDisplayConfirmCopyOri, setDisplayConfirmInputCopyOri, isOri, isOriCopy, checkCopyOri]);
  // #endregion

  // #region ORIデータ新規作成

  const {
    mutateAsync: newOriAsync,
    error: newOriError,
    isError: isNewOriError,
    isLoading: isNewOriloading,
  } = usePostManuscriptsCreateNewOri<AxiosError<ErrorInfo>, unknown>(options);

  const [displayConfirmCreateNewOri, setDisplayConfirmCreateNewOri] = useState(false);
  const [displayNewOriError, setNewOriErrorModal] = useState(false); // oriデータ作成時のエラーモーダルを表示する
  const [displayNewOriSpinner, setNewOriSpinnerModal] = useState(false); // oriデータ作成時のスピナーモーダルを表示する

  // ORIデータ新規作成エラー表示
  useEffect(() => {
    setNewOriErrorModal(isNewOriError);
    setNewOriSpinnerModal(isNewOriloading);
  }, [isNewOriError, isNewOriloading]);

  // ORIデータ新規作成
  const createNewOri = useCallback(async () => {
    const oriRes = await newOriAsync({ productionId: Number(productionId) }); // ORIデータ新規作成 API へのアクセス
    if (oriRes.status === 200) {
      // 原稿編集へリダイレクト
      navigate(`../sections/${productionId}/ps?section_id=${Number(oriRes.data.first_section_id)}`); // 画面遷移
    }
  }, [newOriAsync, productionId, navigate]);

  // #endregion

  // #region エクスポート

  // エクスポート処理定義
  const {
    mutateAsync: translationsExportXmlAsync,
    error: translationsExportXmlError,
    isLoading: translationsExportXmlIsLoading,
  } = usePostTranslationsExportXml<AxiosError<Blob>, PostTranslationsExportXmlMutationResult>(optionsForBinary);

  const [displayTranslationsExportXmlError, setDisplayTranslationsExportXmlError] = useState(false);

  useEffect(() => {
    setDisplayTranslationsExportXmlError(translationsExportXmlError != null);
  }, [translationsExportXmlError]);

  const onExportClicked = useCallback(async () => {
    const response = await translationsExportXmlAsync({
      productionId,
      data: { comment: '', forced_export: true, is_trans_tab: false },
    });
    if (response.data != null) {
      executeDownload(response);
    }
  }, [productionId, translationsExportXmlAsync]);

  // #endregion

  // #region FrameMaker用XMLダウンロード

  // 定義
  const [dispConfiOutputFm, setDispConfiOutputFm] = useState(false);
  const [dispOutputFmErr, setDispOutputFmErr] = useState(false);
  const {
    mutateAsync: outputFmAsync,
    error: outputFmErr,
    isLoading: outputEmIsloading,
  } = usePostManuscriptsOutputFrame<AxiosError<Blob>>(optionsForBinary);

  useEffect(() => {
    setDispOutputFmErr(outputFmErr != null);
  }, [outputFmErr]);

  // ダウンロード処理
  const onOutputFm = useCallback(async () => {
    // setDisplaySpinnerModal(true);
    const response = await outputFmAsync({ productionId });
    if (response.status === 200) {
      executeDownload(response);
      // setDisplaySpinnerModal(false);
    } else {
      // setDisplaySpinnerModal(false);
    }
  }, [productionId, outputFmAsync]);
  // #endregion

  if (msInfoIsLoading) {
    return <WaitComponent />;
  }
  if (msInfoIsError) {
    return <ErrorComponent error={msInfoError} />;
  }

  return (
    <>
      <div className="base-master">
        <div className="master-navi-ms">
          <ul>
            {editable ? (
              <>
                <li className="cursor detailbtn1-2b">
                  <button type="button" className="text-button" onClick={confirmSaving}>
                    <i className="fa-solid fa-floppy-disk" />
                    {t('保存')}
                  </button>
                </li>
                <li className="cursor">
                  <CancelButton editable={editable} cancel={cancel} />
                </li>
              </>
            ) : (
              <li className="cursor detailbtn1-2a">
                <button type="button" className="text-button" onClick={edit}>
                  <span>
                    <i className="fa-solid fa-pen-to-square" />
                    {t('編集')}
                  </span>
                </button>
              </li>
            )}
          </ul>
        </div>
        <div className="master-navi-ms">
          <form>
            <h3 className="mdh2" style={{ padding: '0 0 0 0' }}>
              <span>
                {t('原稿設定情報')}
                <button type="button" className="text-button" onClick={() => setOpen((pre) => !pre)}>
                  <i className={`fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </button>
              </span>
            </h3>
            <SlideToggle open={open}>
              <div className="relativefr">
                <div className="grid5 labip5 pb10">
                  <div>
                    <div>
                      <label>{t('●Navi情報：')}</label>
                    </div>
                  </div>
                  <div>
                    <span className="ck">
                      <input type="checkbox" id="include_navi" {...register('include_navi')} disabled={!editable} />
                      <label htmlFor="include_navi">{t('Navi有')}</label>
                    </span>
                  </div>
                  <div>
                    <div>
                      <label htmlFor="navi_production_no">{t('資料管理No')}</label>
                      <input
                        type="text"
                        id="navi_production_no"
                        {...register('navi_production_no')}
                        disabled={!editable}
                      />
                    </div>
                  </div>
                  <div>
                    <div>
                      <label htmlFor="l1_title_jp">{t('L1(日)')}</label>
                      <input type="text" id="l1_title_jp" {...register('l1_title_jp')} disabled={!editable} />
                    </div>
                  </div>
                  <div>
                    <div>
                      <label htmlFor="l1_title_en">{t('L1(英語)')}</label>
                      <input type="text" id="l1_title_en" {...register('l1_title_en')} disabled={!editable} />
                    </div>
                  </div>
                </div>
                <div className="grid5 labip5">
                  <div>
                    <div>
                      <label style={{ color: isCustomize ? 'red' : 'black' }}>{t('●長体情報：')}</label>
                    </div>
                  </div>
                  <div>
                    <div>
                      <span style={{ width: '120px' }}>
                        <SingleSelect
                          id="apply_stretch"
                          name="apply_stretch"
                          allowEmpty={false}
                          options={[
                            { value: 0, label: t('default') },
                            { value: 1, label: t('customize') },
                          ]}
                          register={register}
                          getValue={(x) => x.value}
                          getLabel={(x) => x.label}
                          isDisabled={!editable}
                        />
                      </span>
                    </div>
                  </div>
                  <div>
                    <div>
                      <label htmlFor="font_stretch">{t('font-stretch')}</label>
                      <input type="text" id="font_stretch" {...register('font_stretch')} disabled={!editable} />
                    </div>
                    {errors.font_stretch?.message && (
                      <p className="errormessage show">{errors.font_stretch?.message}</p>
                    )}
                  </div>
                  <div>
                    <div>
                      <label htmlFor="line_height">{t('line-height')}</label>
                      <input type="text" id="line_height" {...register('line_height')} disabled={!editable} />
                    </div>
                    {errors.line_height?.message && <p className="errormessage show">{errors.line_height?.message}</p>}
                  </div>
                </div>
              </div>
            </SlideToggle>
          </form>
        </div>
        <div className="master-navi-ms navisep pt15">
          <ul>
            {/* F-List取込み */}
            {isImpFlist && (
              <li className="">
                <button
                  type="button"
                  className="text-button"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenFListUploader(true);
                  }}
                  onKeyDown={(e) => {
                    // フォーカス時にスペースボタンで動作するようにする
                    if (e.code === 'Space') {
                      setOpenFListUploader(true);
                    }
                  }}
                >
                  <span>
                    <i className="fa-solid fa-upload" />
                    {t('F-list取込み')}
                  </span>
                </button>
              </li>
            )}

            {/* F-List判定結果 */}
            <li>
              <Link to={`/${language}/productions/base-manual/${productionId}/flist`} target="_blank">
                <span className={isFlist ? 'span-fa-enable' : 'span-fa-disable'}>
                  <i className="fa-solid fa-circle-check" />
                  {t('F-List判定結果')}
                </span>
              </Link>
            </li>
          </ul>
          <ul>
            {/* 原稿編集画面へ移動 */}
            <li>
              {isOri ? (
                <Link
                  to={`/${language}/productions/sections/${productionId}/ps?section_id=${Number(firstSectionId)}`}
                  target="_blank"
                >
                  <span className="span-fa-enable">
                    <i className="fa-solid fa-pen-to-square" />
                    {t('原稿編集画面へ移動')}
                  </span>
                </Link>
              ) : (
                <span className="span-fa-disable">
                  <i className="fa-solid fa-pen-to-square" />
                  {t('原稿編集画面へ移動')}
                </span>
              )}
            </li>
            {/* ORIデータ複製 */}
            <li>
              <button
                type="button"
                className="text-button"
                onClick={() => isOriCopy && setDisplayConfirmStartCreateOri(true)}
              >
                <span className={isOriCopy ? 'span-fa-enable' : 'span-fa-disable'}>
                  <i className="fa-solid fa-copy" />
                  {t('原稿データ複製')}
                </span>
              </button>
            </li>

            {/* ORIデータ新規作成 */}
            <li className="modal-open" onClick={() => isOriNew && setDisplayConfirmCreateNewOri(true)}>
              <span className={isOriNew ? 'span-fa-enable' : 'span-fa-disable'}>
                <i className="fa-solid fa-circle-plus" />
                {t('原稿データ新規作成')}
              </span>
            </li>
          </ul>
          <ul>
            <li>
              <ManuscriptsTabOutputCommand productionId={productionId} isReadOnly={false} />
            </li>
            {/* dOM生成 */}
            {/*
            <li className="">
              <button type="button" className="text-button" onClick={() => onDomDownloadClicked()}>
                <span>
                  <i className="fa-solid fa-download" />
                  {t('dOM生成')}
                </span>
              </button>
            </li>
            */}
            {/* New WebOM生成 */}
            <li className="">
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  setWebType(31);
                  void onWebOmDownloadClicked();
                }}
              >
                <span>
                  <i className="fa-solid fa-download" />
                  {t('New WebOM生成')}
                </span>
              </button>
            </li>

            {/* WebOM生成 */}
            <li className="">
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  setWebType(30);
                  void onWebOmDownloadClicked();
                }}
              >
                <span>
                  <i className="fa-solid fa-download" />
                  {t('WebOM生成')}
                </span>
              </button>
            </li>
          </ul>
        </div>
        <h3 className="mdh2" style={{ padding: '20px 0px 0px 0px' }} onClick={() => setOpenPs(!openPs)}>
          <span>
            {t('PS改廃一覧')}
            <i className={`fa-solid ${openPs ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
          </span>
        </h3>
        <SlideToggle open={openPs}>
          <ProductionManuscriptsPsSituationList key="pssituationlist" productionId={productionId} />
        </SlideToggle>

        <h3 className="mdh2" style={{ padding: '20px 0px 0px 0px' }} onClick={() => setOpenRev(!openRev)}>
          <span>
            {t('改訂履歴一覧')}
            <i className={`fa-solid ${openRev ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
          </span>
        </h3>
        <SlideToggle open={openRev}>
          <div className="relativefr">
            <div className="grid3 labip2 mt20 mb10">
              <div style={{ marginLeft: '20px' }}>
                <label htmlFor="chapter_number_ids">{t('章')}</label>
                <SingleSelect
                  id="chapter_number_ids"
                  name={'chapter_number_ids' as never}
                  allowEmpty={false}
                  options={chapterData.data?.data.chapter_numbers ?? []}
                  register={register}
                  getValue={(x) => x.chapter_number_id}
                  getLabel={(x) => x.chapter_name}
                  onChange={(e) => setSelectChapterNumberId(Number(e.target.value))}
                  additionalClassName="ms-rev-chapter"
                />
              </div>
            </div>
          </div>
          <div className="tableWrapper14">
            <table className="table14">
              <thead>
                <tr>
                  <th>
                    {t('ゲート')}
                    <button type="button" className="text-button" onClick={() => onSortClicked('gate')}>
                      <i className="fa-solid fa-sort" />
                    </button>
                  </th>
                  <th>
                    {t('章')}
                    <button type="button" className="text-button" onClick={() => onSortClicked('chapter_number_id')}>
                      <i className="fa-solid fa-sort" />
                    </button>
                  </th>
                  <th>
                    {t('タイトル')}
                    <button type="button" className="text-button" onClick={() => onSortClicked('title')}>
                      <i className="fa-solid fa-sort" />
                    </button>
                  </th>
                  <th>
                    {t('担当')}
                    <button type="button" className="text-button" onClick={() => onSortClicked('user_name')}>
                      <i className="fa-solid fa-sort" />
                    </button>
                  </th>
                  <th>
                    {t('進捗率')}
                    <button type="button" className="text-button" onClick={() => onSortClicked('completion_rate')}>
                      <i className="fa-solid fa-sort" />
                    </button>
                  </th>
                  <th>
                    {t('更新日')}
                    <button type="button" className="text-button" onClick={() => onSortClicked('updated_date')}>
                      <i className="fa-solid fa-sort" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {revDataSorted.map((x) => (
                  <tr key={x.manuscript_id}>
                    <td>{x.gate}</td>
                    <td>{x.chapter_number_id}</td>
                    <td>
                      {x.section_id !== undefined && (
                        <Link
                          to={`/${language}/productions/sections/${productionId}/ps?section_id=${Number(x.section_id)}`}
                          target="_blank"
                        >
                          {x.title}
                        </Link>
                      )}
                      {x.section_id === undefined && <span>{x.title}</span>}
                    </td>
                    <td>{x.user_name}</td>
                    <td>{x.completion_rate}</td>
                    <td>{x.updated_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SlideToggle>
        <div className="master-navi-ms mt20">
          <ul style={{ justifyContent: 'flex-end' }}>
            <li>
              <button type="button" className="text-button" onClick={() => onExportClicked()}>
                <i className="fa-solid fa-download" />
                {t('開発者用XML')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* 改訂履歴取得エラー表示 */}
      {revErrorModal && <ErrorModal error={revError} onClickOk={() => setRevErrorModal(false)} />}

      {/* ロックエラー表示 */}
      {displayLockError && <ErrorModal error={lockError} onClickOk={() => setDisplayLockError(false)} />}
      {/* ロックキャンセルエラー表示 */}
      {displayCancelError && <ErrorModal error={cancelError} onClickOk={() => setDisplayCancelError(false)} />}
      {/* 保存時のエラー表示 */}
      {displaySaveError && <ErrorModal onClickOk={() => setDisplaySaveError(false)} error={saveError} />}
      {/* スピナーモーダル */}
      {displaySpinnerModal && <SpinnerModal />}
      {/* バリデーションエラーモーダル */}
      {displayValidationError && (
        <OkModal onClickOk={() => setDisplayValidationError(false)}>{t('バリデーションエラーです。')}</OkModal>
      )}
      {/* 他のタブがロックされている場合のエラー */}
      {displayAnotherTabLockError && editModeTabIndex != null && (
        <AnotherTabLockingErrorModal
          editModeTabIndex={editModeTabIndex}
          onClickOk={() => setDisplayAnotherTabLockError(false)}
        />
      )}

      {/* 保存確認メッセージ */}
      {displayConfirmSavingMessage && (
        <ConfirmModal onClickCancel={() => setDisplayConfirmSavingMessage(false)} onClickOk={submit}>
          <div>{t('データを更新しますか？')}</div>
        </ConfirmModal>
      )}
      {/* 保存完了メッセージ */}
      {displayCompleteSavingMessage && (
        <OkModal onClickOk={() => setDisplayCompleteSavingMessage(false)}>
          <div>{t('保存しました。')}</div>
        </OkModal>
      )}
      {/* dOM生成確認(エラーあり) */}
      {displayConfirmDomDownloadErr && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmDomDownloadErr(false)}
          onClickOk={async () => {
            setDisplayConfirmDomDownloadErr(false);
            await domDownload();
          }}
        >
          {t('エラーが存在しますが、dOM生成を行いますか？')}
        </ConfirmModal>
      )}
      {/* dOM生成確認(エラーなし) */}
      {displayConfirmDomDownload && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmDomDownload(false)}
          onClickOk={async () => {
            setDisplayConfirmDomDownload(false);
            await domDownload();
          }}
        >
          {t('dOM生成を行いますか？')}
        </ConfirmModal>
      )}
      {displayManuscriptsCreateDomCheckError && (
        <ErrorModal
          error={manuscriptsCreateDomCheckError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayManuscriptsCreateDomCheckError(false);
          }}
        />
      )}
      {displayManuscriptsCreateDomError && (
        <BlobErrorModal
          error={manuscriptsCreateDomError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayManuscriptsCreateDomError(false);
          }}
        />
      )}

      {/* dOM生成確認(エラーあり) */}
      {displayConfirmWebomDownloadErr && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmWebomDownloadErr(false)}
          onClickOk={async () => {
            setDisplayConfirmWebomDownloadErr(false);
            await webomDownload();
          }}
        >
          {t('エラーが存在しますが、WebOM生成を行いますか？')}
        </ConfirmModal>
      )}
      {/* WebOM生成確認(エラーなし) */}
      {displayConfirmWebomDownload && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmWebomDownload(false)}
          onClickOk={async () => {
            setDisplayConfirmWebomDownload(false);
            await webomDownload();
          }}
        >
          {t('WebOM生成を行いますか？')}
        </ConfirmModal>
      )}
      {displayManuscriptsCreateWebomCheckError && (
        <ErrorModal
          error={manuscriptsCreateWebomCheckError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayManuscriptsCreateWebomCheckError(false);
          }}
        />
      )}
      {displayManuscriptsCreateWebOmError && (
        <BlobErrorModal
          error={manuscriptsCreateWebOmError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayManuscriptsCreateWebOmError(false);
          }}
        />
      )}
      {/* F-list取込みダイアログ */}
      {productionId != null && (
        <FListUploadModal
          active={openFListUploader}
          productionId={productionId}
          setIsFlist={setIsFlist}
          setIsOri={setIsOri}
          onClose={() => setOpenFListUploader(false)}
        />
      )}
      {displayConfirmStartCreateOri && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmStartCreateOri(false)}
          onClickOk={() => {
            setDisplayConfirmStartCreateOri(false);
            void onClickCopyOri();
          }}
        >
          {t('ベース本を複製して原稿データを作成しますか？')}
        </ConfirmModal>
      )}
      {displayConfirmCopyOri && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmCopyOri(false)}
          onClickOk={() => {
            setDisplayConfirmCopyOri(false);
            void checkCopyOri();
          }}
        >
          {t('既存のデータがすべて上書きされ、手で修正した内容もクリアされますがよろしいですか？')}
        </ConfirmModal>
      )}
      {displayConfirmInputCopyOri && (
        <ConfirmInputModal
          keyWord="複製"
          onClickCancel={() => setDisplayConfirmInputCopyOri(false)}
          onClickOk={() => {
            setDisplayConfirmInputCopyOri(false);
            void checkCopyOri();
          }}
        >
          {t(
            '編集中の原稿データが存在します。そのデータを消去してベース本を複製する場合は、テキスト入力フィールドに「複製」と入力します。'
          )}
        </ConfirmInputModal>
      )}
      {/* 複製前チェック */}
      {displayConfirmCheckCopyOri && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmCheckCopyOri(false)}
          onClickOk={() => {
            setDisplayConfirmCheckCopyOri(false);
            void createOri();
          }}
        >
          {oriCheckError?.response?.data.message}
        </ConfirmModal>
      )}
      {/* ORIデータ複製時スピナーモーダルの表示 */}
      {displayOriSpinner && <SpinnerModal />}
      {/* ORIデータ複製前チェック時エラーの表示 */}
      {displayCheckOriError && (
        <ErrorModal
          error={oriCheckError}
          onClickOk={() => {
            setOriSpinnerModal(false);
            setCheckOriErrorModal(false);
          }}
        />
      )}
      {/* ORIデータ複製時エラーの表示 */}
      {displayOriError && (
        <ErrorModal
          error={oriError}
          onClickOk={() => {
            setOriSpinnerModal(false);
            setOriErrorModal(false);
          }}
        />
      )}
      {/* ORIデータ新規作成確認 */}
      {displayConfirmCreateNewOri && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirmCreateNewOri(false)}
          onClickOk={() => {
            setDisplayConfirmCreateNewOri(false);
            void createNewOri();
          }}
        >
          {t('原稿データを新規作成しますか？')}
        </ConfirmModal>
      )}
      {/* ORIデータ新規作成時スピナーモーダルの表示 */}
      {displayNewOriSpinner && <SpinnerModal />}
      {/* ORIデータ新規作成時エラーの表示 */}
      {displayNewOriError && (
        <ErrorModal
          error={newOriError}
          onClickOk={() => {
            setNewOriSpinnerModal(false);
            setNewOriErrorModal(false);
          }}
        />
      )}
      {/* 参照リストエラー */}
      {displayManuscriptsOutputReferError && (
        <BlobErrorModal
          error={manuscriptsOutputReferError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayManuscriptsOutputReferError(false);
          }}
        />
      )}

      {/* エクスポート時のスピナー */}
      {translationsExportXmlIsLoading && <SpinnerModal />}
      {/* エクスポートエラー */}
      {displayTranslationsExportXmlError && (
        <BlobErrorModal
          onClickOk={() => {
            setDisplayTranslationsExportXmlError(false);
          }}
          error={translationsExportXmlError}
        />
      )}

      {/* FrameMaker用XML出力確認メッセージ */}
      {dispConfiOutputFm && (
        <ConfirmModal
          onClickCancel={() => setDispConfiOutputFm(false)}
          onClickOk={() => {
            void onOutputFm();
            setDispConfiOutputFm(false);
          }}
        >
          <div>{t('FrameMaker用XMLを出力しますか？')}</div>
        </ConfirmModal>
      )}

      {/* スピナーモーダル */}
      {outputEmIsloading && <SpinnerModal />}
      {/* FrameMaker用XMLエラー */}
      {dispOutputFmErr && <BlobErrorModal onClickOk={() => setDispOutputFmErr(false)} error={outputFmErr} />}
    </>
  );
}

export default ProductionManuscriptsTab;
